import { getWeldBuffersForComponents } from 'features/assetModeling'
import {
  GeckoBinnedDataBufferSerializable,
  serializeBinnedDataBuffer,
  useCdfDataFetcher,
} from 'features/dataLayers'
import { getTMinMethod } from 'features/fde/tminMethod/tminMethodCallback'
import { useRolodexEntries } from 'features/rolodex'
import compact from 'lodash/compact'
import {
  ComponentId,
  RugComponentStats,
  RugComponentStatsRaw,
  RugSectionStats,
  RugSectionStatsRaw,
  SectionableRaw,
} from 'pages/reporting/reports/rug/types'
import { sectionableToRaw } from 'pages/reporting/reports/rug/utils/sectionable'
import {
  deserializeRUGComponentStats,
  deserializeRUGSectionStats,
} from 'pages/reporting/reports/rug/utils/serializeRUGStats'
import { SectionStatsWorkerProps } from 'pages/reporting/reports/rug/workers/sectionStats.worker'
import SectionStatsWorker from 'pages/reporting/reports/rug/workers/sectionStats.worker?worker'
import { useCallback, useMemo } from 'react'
import useSWRImmutable from 'swr/immutable'
import {
  ForecastComponentConfig,
  GeckoAssetModel,
  GeckoComponentDataFrame,
  GeckoComponentModel,
  GeckoEntry,
  GeckoMeasurement,
  GeckoMeasurementUnit,
  GeckoSource,
  GeckoTMinMethod,
  RolodexEntryJson,
  RolodexEntryType,
  Sectionable,
} from 'types'

type ComponentBoundsRaw = Record<string, unknown>
type WeldBufferRaw = Record<string, unknown>[]

function getStatsSourceVersion(
  entry: Pick<GeckoEntry, 'id' | 'generation' | 'updated_at'>
) {
  return {
    id: entry.id,
    generation: entry.generation,
    updatedAt: entry.updated_at,
  }
}

export interface GetStatsBySectionProps {
  sectionsByComponent: Map<ComponentId, Sectionable | undefined>
  selectedComponentModels: readonly GeckoComponentModel[]
  relevantThicknessCDFs: GeckoComponentDataFrame[] | undefined
  assetModel: GeckoAssetModel | undefined
  componentDataFrames: readonly GeckoComponentDataFrame[]
  isLoadingData: boolean
  userFacingThicknessUnit: GeckoMeasurementUnit
}

function getSectionableStatsCacheKey(
  sectionsByComponent: Map<ComponentId, Sectionable | undefined>
) {
  return Array.from(sectionsByComponent.entries()).map(
    ([componentId, sectionable]) => ({
      componentId,
      sectionable: sectionable ? sectionableToRaw(sectionable) : undefined,
    })
  )
}

export const useGetStatsBySection = ({
  sectionsByComponent,
  selectedComponentModels,
  relevantThicknessCDFs,
  assetModel,
  componentDataFrames,
  isLoadingData,
  userFacingThicknessUnit,
}: GetStatsBySectionProps) => {
  const componentModelIds = useMemo(() => {
    return selectedComponentModels.map((componentModel) => componentModel.id)
  }, [selectedComponentModels])

  const {
    entries: forecastComponentConfigs,
    isLoading: isLoadingForecastComponentConfigs,
  } = useRolodexEntries(
    isLoadingData
      ? undefined
      : {
          type: RolodexEntryType.FDE_FORECAST_COMPONENT_CONFIG,
          tags: {
            [RolodexEntryType.COMPONENT]: componentModelIds,
          },
        }
  )

  const weldBufferMap = useMemo(() => {
    return getWeldBuffersForComponents(
      selectedComponentModels,
      GeckoMeasurement.CreateFromValue(6, GeckoMeasurementUnit.Inches)
    )
  }, [selectedComponentModels])

  const cdfDataFetcher = useCdfDataFetcher()

  const tminMethodByComponent = useMemo(() => {
    const tMinMethodByComponent = new Map<ComponentId, GeckoTMinMethod>()
    selectedComponentModels.forEach((componentModel) => {
      const relevantThicknessCDF = relevantThicknessCDFs?.find(
        (cdf) => cdf.tags.component === componentModel.id
      )
      const forecastComponentConfig = forecastComponentConfigs?.find(
        (config) => config.tags.component === componentModel.id
      ) as ForecastComponentConfig | undefined
      const tMinMethod = getTMinMethod(
        assetModel,
        relevantThicknessCDF?.tags.component ?? '',
        relevantThicknessCDF,
        forecastComponentConfig
      )
      if (tMinMethod) {
        tMinMethodByComponent.set(componentModel.id as ComponentId, tMinMethod)
      }
    })
    return tMinMethodByComponent
  }, [
    assetModel,
    relevantThicknessCDFs,
    forecastComponentConfigs,
    selectedComponentModels,
  ])

  const tMinCDFs = useMemo(() => {
    return compact(
      selectedComponentModels.map((componentModel) => {
        const tminMethod = tminMethodByComponent.get(
          componentModel.id as ComponentId
        )
        if (!tminMethod || tminMethod === GeckoTMinMethod.TMinByShellCourse) {
          return undefined
        }

        return componentDataFrames.find(
          (cdf) =>
            cdf.data.params.tmin_method.id === tminMethod.id &&
            cdf.data.params.source.id === GeckoSource.TMin.id &&
            cdf.tags.component === componentModel.id
        )
      })
    )
  }, [tminMethodByComponent, componentDataFrames, selectedComponentModels])

  const fetchBinnedDataAndCreateParams = useCallback(async () => {
    if (!relevantThicknessCDFs || relevantThicknessCDFs.length === 0) {
      return undefined
    }

    if (isLoadingData) {
      return undefined
    }

    const sectionsByComponentRaw: Record<ComponentId, SectionableRaw> = {}
    const relevantThicknessCDFsRaw: RolodexEntryJson[] = []
    const componentsBoundsRaw: Record<ComponentId, ComponentBoundsRaw> = {}
    const binnedDataBufferSerializable: Record<
      ComponentId,
      GeckoBinnedDataBufferSerializable
    > = {}
    const tminCDFsRaw: RolodexEntryJson[] = []
    const tminBinnedDataBufferSerializable: Record<
      ComponentId,
      GeckoBinnedDataBufferSerializable
    > = {}
    const weldBuffersRaw: Record<ComponentId, WeldBufferRaw> = {}

    let hasValidComponents = false
    const tMinMethodIdByComponent: Record<ComponentId, string> =
      tminMethodByComponent.entries().reduce(
        (acc, [componentId, tminMethod]) => {
          acc[componentId] = tminMethod.id
          return acc
        },
        {} as Record<ComponentId, string>
      )

    for (const componentModel of selectedComponentModels) {
      const componentId = componentModel.id as ComponentId
      const sectionable = sectionsByComponent.get(componentId)

      const relevantThicknessCDF = relevantThicknessCDFs.find(
        (cdf) => cdf.tags.component === componentModel.id
      )

      if (!relevantThicknessCDF) {
        continue
      }

      const thicknessBinnedDataBuffer =
        await cdfDataFetcher(relevantThicknessCDF)

      if (!thicknessBinnedDataBuffer) {
        continue
      }

      let tminBinnedDataBuffer = undefined
      const tminCDF = tMinCDFs?.find(
        (cdf) => cdf.tags.component === componentModel.id
      )

      if (tminCDF) {
        tminBinnedDataBuffer = await cdfDataFetcher(tminCDF)
        tminCDFsRaw.push(tminCDF.raw)
        if (tminBinnedDataBuffer) {
          tminBinnedDataBufferSerializable[componentId] =
            serializeBinnedDataBuffer(tminBinnedDataBuffer)
        }
      }

      const weldBuffers = weldBufferMap.get(componentModel.id) ?? []

      if (sectionable) {
        sectionsByComponentRaw[componentId] = sectionableToRaw(sectionable)
      }
      relevantThicknessCDFsRaw.push(relevantThicknessCDF.raw)
      componentsBoundsRaw[componentId] = componentModel.componentBounds.toJSON()
      binnedDataBufferSerializable[componentId] = serializeBinnedDataBuffer(
        thicknessBinnedDataBuffer
      )
      weldBuffersRaw[componentId] = weldBuffers.map((wb) => wb.toJSON())

      hasValidComponents = true
    }

    if (!hasValidComponents || !assetModel) {
      return undefined
    }

    const workerProps: SectionStatsWorkerProps = {
      assetModelRaw: assetModel.raw,
      sectionsByComponentRaw,
      relevantThicknessCDFsRaw,
      componentsBoundsRaw,
      binnedDataBufferSerializable,
      tminCDFsRaw: tminCDFsRaw.length > 0 ? tminCDFsRaw : undefined,
      tminBinnedDataBufferSerializable:
        Object.keys(tminBinnedDataBufferSerializable).length > 0
          ? tminBinnedDataBufferSerializable
          : undefined,
      weldBuffersRaw,
      tMinMethodIdByComponent:
        Object.keys(tMinMethodIdByComponent).length > 0
          ? tMinMethodIdByComponent
          : undefined,
      userFacingThicknessUnitId: userFacingThicknessUnit.id,
    }

    return workerProps
  }, [
    relevantThicknessCDFs,
    selectedComponentModels,
    sectionsByComponent,
    tminMethodByComponent,
    tMinCDFs,
    weldBufferMap,
    cdfDataFetcher,
    assetModel,
    isLoadingData,
    userFacingThicknessUnit,
  ])

  const calculateSectionStatsInWebWorker = useCallback(async () => {
    const workerProps = await fetchBinnedDataAndCreateParams()

    if (!workerProps) {
      return undefined
    }

    return new Promise<{
      sectionStatsRaw: Record<ComponentId, RugSectionStatsRaw[]>
      componentStatsRaw: Record<ComponentId, RugComponentStatsRaw>
    }>((resolve, reject) => {
      try {
        const worker = new SectionStatsWorker()

        worker.onmessage = (message) => {
          worker.terminate()
          resolve(
            message.data as {
              sectionStatsRaw: Record<ComponentId, RugSectionStatsRaw[]>
              componentStatsRaw: Record<ComponentId, RugComponentStatsRaw>
            }
          )
        }

        worker.onerror = (error) => {
          console.error('Section stats worker error:', error)
          worker.terminate()
          reject(error)
        }

        worker.postMessage(workerProps)
      } catch (error) {
        console.error('Failed to create section stats worker:', error)
        reject(error)
      }
    })
  }, [fetchBinnedDataAndCreateParams])

  const swrKey = useMemo(() => {
    if (
      !relevantThicknessCDFs ||
      relevantThicknessCDFs.length === 0 ||
      selectedComponentModels.length === 0
    ) {
      return null
    }

    return {
      assetModelVersion: assetModel
        ? getStatsSourceVersion(assetModel)
        : undefined,
      thicknessCDFVersions: relevantThicknessCDFs.map(getStatsSourceVersion),
      componentIds: selectedComponentModels.map((cm) => cm.id),
      sectionables: getSectionableStatsCacheKey(sectionsByComponent),
      tminCDFVersions: tMinCDFs.map(getStatsSourceVersion),
      tminMethods: Array.from(
        tminMethodByComponent,
        ([componentId, method]) => ({ componentId, methodId: method.id })
      ),
      isLoadingForecastComponentConfigs,
      isLoadingData,
      forecastComponentConfigVersions:
        forecastComponentConfigs?.map(getStatsSourceVersion) ?? [],
      userFacingThicknessUnitId: userFacingThicknessUnit.id,
    }
  }, [
    assetModel,
    relevantThicknessCDFs,
    selectedComponentModels,
    sectionsByComponent,
    tMinCDFs,
    tminMethodByComponent,
    isLoadingForecastComponentConfigs,
    isLoadingData,
    forecastComponentConfigs,
    userFacingThicknessUnit,
  ])

  const { data, isLoading: isCalculating } = useSWRImmutable(
    swrKey,
    swrKey ? calculateSectionStatsInWebWorker : null
  )

  return useMemo(() => {
    if (!data) {
      return {
        statsBySection: undefined,
        statsByComponent: undefined,
        isLoading: isCalculating,
      }
    }

    const statsBySection = new Map<ComponentId, Map<string, RugSectionStats>>()
    const statsByComponent = new Map<ComponentId, RugComponentStats>()

    if (data.sectionStatsRaw) {
      Object.entries(data.sectionStatsRaw).forEach(
        ([componentId, sectionStatsRaw]) => {
          const statsMap = new Map<string, RugSectionStats>()

          sectionStatsRaw.forEach((statsRaw) => {
            const stats = deserializeRUGSectionStats(statsRaw)
            statsMap.set(stats.section.name, stats)
          })

          statsBySection.set(componentId as ComponentId, statsMap)
        }
      )
    }

    if (data.componentStatsRaw) {
      Object.entries(data.componentStatsRaw).forEach(
        ([componentId, componentStatsRaw]) => {
          const stats = deserializeRUGComponentStats(componentStatsRaw)
          statsByComponent.set(componentId as ComponentId, stats)
        }
      )
    }

    return {
      statsBySection,
      statsByComponent,
      isLoading: isCalculating,
    }
  }, [data, isCalculating])
}
